import type { FC } from 'react';
import { useState } from 'react';
import { Helmet } from 'react-helmet';
import { useHistory, useLocation } from 'react-router-dom';
import { Alert } from '@/components/alert';
import { ButtonAnt } from '@/components/buttons/button/ButtonAnt';
import { Form, Input } from '@/components/form';
import { FormattedMessage, useIntl } from '@/components/formatted_message/FormattedMessage';
import { Icon } from '@/components/icon';
import { Flex } from '@/components/layouting';
import { Typography } from '@/components/typography';
import { useAuthContext } from '@/contexts/AuthContext';
import { StyledAuthLayout } from '@/routes/auth/components/Containers';
import { EnmonLogo } from '@/routes/auth/components/EnmonLogo';
import { StyledFormWrapper } from '@/routes/auth/components/FormContainer';
import { getPasswordResetToken } from '@/routes/auth/utils/identifiedUserStorage';
import { SET_PASSWORD_FORM_INITIAL_VALUES } from './constants';
import { Page as messages } from './messages';
import { getErrorMessage } from './utils/getErrorMessage';

const SET_PASSWORD_FORM_ID = 'set-password-form';

const { Headline, Paragraph } = Typography;

interface NewPasswordFormValues {
  password: string;
  passwordCheck: string;
}

export const SetPasswordPage: FC = () => {
  const intl = useIntl();
  const { search } = useLocation();
  const history = useHistory();
  const params = new URLSearchParams(search);
  const [passwordVisible, setPasswordVisible] = useState(false);

  // Check for token in URL (magic link) or sessionStorage (OTP flow)
  const urlToken = params.get('token');
  const sessionToken = getPasswordResetToken();
  const token = urlToken || sessionToken || '';

  const [form] = Form.useForm<NewPasswordFormValues>();

  const { isError: hasError, error, resetMutation, isLoading, setPassword } = useAuthContext();

  const handleSubmit = ({ password, passwordCheck }: NewPasswordFormValues) => {
    setPassword({ password, passwordCheck, token });
  };

  const handleCancel = () => {
    history.push('/');
    resetMutation.setPassword();
  };

  return (
    <StyledAuthLayout>
      <Helmet>
        <title>{intl.formatMessage(messages.title)}</title>
      </Helmet>
      <EnmonLogo />
      <Flex gap={24} vertical>
        {!token && (
          <Headline>
            <FormattedMessage {...messages.tokenInvalid} />
          </Headline>
        )}

        {token && (
          <>
            <Headline data-cy="setPassword-title">
              <FormattedMessage {...messages.title} />
            </Headline>
            <Paragraph data-cy="setPassword-description">
              <FormattedMessage {...messages.hint} />
            </Paragraph>

            {Boolean(hasError && error.setPassword) && (
              <Alert
                data-cy="setPassword-error"
                data-test="setPassword-error"
                description={<FormattedMessage {...getErrorMessage(error.setPassword)} />}
                showIcon
                type="error"
              />
            )}

            <StyledFormWrapper>
              <Form
                id={SET_PASSWORD_FORM_ID}
                form={form}
                initialValues={SET_PASSWORD_FORM_INITIAL_VALUES}
                layout="vertical"
                onFinish={handleSubmit}
              >
                <Form.Item
                  label={intl.formatMessage(messages.pass)}
                  name="password"
                  required={false}
                  rules={[
                    {
                      required: true,
                      message: intl.formatMessage(messages.passwordRequired),
                      validateTrigger: 'onSubmit',
                    },
                  ]}
                >
                  <Input
                    autoFocus
                    data-cy="setPassword-password"
                    data-test="setPassword-password"
                    placeholder={intl.formatMessage(messages.pass)}
                    suffix={
                      <Icon
                        iconString={passwordVisible ? 'eye' : 'eyeSlash'}
                        onClick={() => setPasswordVisible(!passwordVisible)}
                        style={{ cursor: 'pointer' }}
                      />
                    }
                    type={passwordVisible ? 'text' : 'password'}
                  />
                </Form.Item>
                <Form.Item
                  dependencies={['password']}
                  label={intl.formatMessage(messages.passCheck)}
                  name="passwordCheck"
                  required={false}
                  rules={[
                    {
                      required: true,
                      message: intl.formatMessage(messages.passwordCheckRequired),
                      validateTrigger: 'onSubmit',
                    },
                    ({ getFieldValue }) => ({
                      validator: async (_, value) => {
                        if (value && getFieldValue('password') !== value) {
                          throw new Error(intl.formatMessage(messages.passwordMatchPasswordCheck));
                        }
                        return await Promise.resolve();
                      },
                      validateTrigger: 'onSubmit',
                    }),
                  ]}
                >
                  <Input
                    data-cy="setPassword-passwordCheck"
                    data-test="setPassword-passwordCheck"
                    placeholder={intl.formatMessage(messages.passCheck)}
                    suffix={
                      <Icon
                        iconString={passwordVisible ? 'eye' : 'eyeSlash'}
                        onClick={() => setPasswordVisible(!passwordVisible)}
                        style={{ cursor: 'pointer' }}
                      />
                    }
                    type={passwordVisible ? 'text' : 'password'}
                  />
                </Form.Item>
              </Form>
            </StyledFormWrapper>

            <Flex className="w-100" gap={16} vertical>
              <ButtonAnt
                block
                data-cy="setPassword-submit-button"
                data-test="setPassword-submit-button"
                form={SET_PASSWORD_FORM_ID}
                htmlType="submit"
                loading={isLoading}
                size="large"
                type="primary"
              >
                <FormattedMessage {...messages.send} />
              </ButtonAnt>
              <ButtonAnt
                block
                data-cy="setPassword-cancel-button"
                data-test="setPassword-cancel-button"
                onClick={handleCancel}
                size="large"
              >
                <FormattedMessage {...messages.cancel} />
              </ButtonAnt>
            </Flex>
          </>
        )}
      </Flex>
    </StyledAuthLayout>
  );
};
